import { useEffect, useRef, useState } from "react";

import { subscribeToLoading } from "../lib/progress.js";

/**
 * THE THIN BAR ACROSS THE TOP OF THE APP — the one Google Ads and Meta Ads run while a
 * report loads.
 *
 * It is a PROGRESS ILLUSION, not a measurement. A browser `fetch`/XHR reports no
 * percentage, so a truthful bar would only ever be a spinner. Instead this eases toward a
 * ceiling (~90%) while requests are in flight and SNAPS to 100% the moment the last one
 * settles — which reads as "working… done" without ever printing a number it cannot know.
 *
 * It is driven by lib/progress (the count of in-flight requests), NOT by any screen's
 * loading flag. So a SINGLE instance, mounted once at the app root, covers every fetch the
 * dashboard makes — reports, store switches, export polling — with no per-screen wiring.
 */

/** The bar never fills itself past this; only a completed load is allowed to reach 100. */
const TRICKLE_CEILING = 90;
/** How often the trickle advances while a request is in flight. */
const TICK_MS = 200;
/** How long the finished bar and its fade are held on screen before resetting to empty. */
const FADE_MS = 220;

export function TopProgressBar(): React.JSX.Element {
  // `visible` drives opacity (mounts stay cheap — one always-present div); `progress` is
  // the 0–100 width the CSS transition animates between.
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0);

  // Timer handles live in refs so the single subscription effect can start and clear them
  // across many active/idle edges without ever re-subscribing.
  const trickle = useRef<ReturnType<typeof setInterval> | null>(null);
  const fade = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const stopTrickle = (): void => {
      if (trickle.current !== null) {
        clearInterval(trickle.current);
        trickle.current = null;
      }
    };
    const cancelFade = (): void => {
      if (fade.current !== null) {
        clearTimeout(fade.current);
        fade.current = null;
      }
    };

    const unsubscribe = subscribeToLoading((active) => {
      if (active) {
        // A load began (or is still running). Abort any pending fade-out from a previous
        // load, show the bar, and give it a small head start so the first paint is a
        // visible sliver rather than a zero-width nothing.
        cancelFade();
        setVisible(true);
        setProgress((current) => (current < 8 ? 8 : current));
        if (trickle.current === null) {
          trickle.current = setInterval(() => {
            // Diminishing steps: the closer to the ceiling, the smaller the advance — the
            // familiar "almost there" easing. It approaches but never reaches the ceiling,
            // so only a completed load can finish the bar.
            setProgress((current) => {
              if (current >= TRICKLE_CEILING) return current;
              return current + Math.max(0.4, (TRICKLE_CEILING - current) * 0.12);
            });
          }, TICK_MS);
        }
      } else {
        // The last request settled. Fill to 100 so the completion is seen, then fade out
        // and — only once invisible — reset the width to 0 so the drop back to empty is
        // never on screen for the next load.
        stopTrickle();
        setProgress(100);
        cancelFade();
        fade.current = setTimeout(() => {
          setVisible(false);
          fade.current = setTimeout(() => {
            setProgress(0);
            fade.current = null;
          }, FADE_MS);
        }, FADE_MS);
      }
    });

    return () => {
      unsubscribe();
      stopTrickle();
      cancelFade();
    };
  }, []);

  return (
    <div
      // Pinned above everything, full width. `pointer-events-none` so it can never
      // swallow a click meant for the header sitting directly beneath it. Decorative, so
      // hidden from assistive tech — the loading state screens announce lives on the
      // report table's `aria-busy`, not on this cosmetic bar.
      className="pointer-events-none fixed inset-x-0 top-0 z-[100] h-[3px]"
      aria-hidden="true"
    >
      <div
        className="bg-primary h-full origin-left rounded-r-full transition-[width,opacity] duration-200 ease-out"
        style={{
          width: `${String(progress)}%`,
          opacity: visible ? 1 : 0,
          // A soft leading glow — the touch that makes it read as a live loading bar and
          // not a flat rule. Tinted from --primary so it tracks the theme (incl. dark).
          boxShadow: visible ? "0 0 8px var(--color-primary), 0 0 3px var(--color-primary)" : "none",
        }}
      />
    </div>
  );
}
